export interface EvolutionInstance {
  instance?: {
    instanceName?: string;
    state?: string;
  };
  name?: string;
  instanceName?: string;
  state?: string;
  connectionStatus?: string;
  owner?: string;
  profileName?: string;
  profilePictureUrl?: string;
  [key: string]: unknown;
}

export interface WhatsAppBackendConfig {
  success: boolean;
  configured: boolean;
  apiUrl: string;
  authEnabled: boolean;
}

export interface QrResponse {
  success: boolean;
  qrCode: string;
  pairingCode: string;
  raw: unknown;
}

const DEFAULT_BACKEND_URL = process.env.REACT_APP_WHATSAPP_BACKEND_URL || 'https://playkids-whatsapp-evolution.fly.dev';
const URL_STORAGE_KEY = 'playkids_whatsapp_backend_url';
const TOKEN_STORAGE_KEY = 'playkids_whatsapp_backend_token';

const getStoredValue = (key: string, fallback: string) => {
  if (typeof window === 'undefined') return fallback;
  const value = window.localStorage.getItem(key) || fallback;
  if (key === URL_STORAGE_KEY && /^https?:\/\/(localhost|127\.0\.0\.1):8080/i.test(value)) {
    window.localStorage.removeItem(key);
    return fallback;
  }
  return value;
};

const setStoredValue = (key: string, value: string) => {
  if (typeof window === 'undefined') return;
  if (value.trim()) {
    window.localStorage.setItem(key, value.trim());
  } else {
    window.localStorage.removeItem(key);
  }
};

const request = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
  const backendUrl = getStoredValue(URL_STORAGE_KEY, DEFAULT_BACKEND_URL).replace(/\/+$/, '');
  const token = getStoredValue(TOKEN_STORAGE_KEY, '');

  const response = await fetch(`${backendUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'X-Admin-Token': token } : {}),
      ...(options.headers || {})
    }
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = data?.error || data?.message || `Erro HTTP ${response.status}`;
    throw new Error(message);
  }

  return data as T;
};

export const evolutionWhatsAppService = {
  getBackendUrl() {
    return getStoredValue(URL_STORAGE_KEY, DEFAULT_BACKEND_URL);
  },

  setBackendUrl(url: string) {
    setStoredValue(URL_STORAGE_KEY, url);
  },

  getToken() {
    return getStoredValue(TOKEN_STORAGE_KEY, '');
  },

  setToken(token: string) {
    setStoredValue(TOKEN_STORAGE_KEY, token);
  },

  async getConfig(): Promise<WhatsAppBackendConfig> {
    return request<WhatsAppBackendConfig>('/api/whatsapp/config');
  },

  async listInstances(): Promise<EvolutionInstance[]> {
    const data = await request<{ success: boolean; instances: EvolutionInstance[] }>('/api/whatsapp/instances');
    return Array.isArray(data.instances) ? data.instances : [];
  },

  async createInstance(instanceName: string): Promise<QrResponse> {
    const data = await request<{ success: boolean; qr: QrResponse }>('/api/whatsapp/instances', {
      method: 'POST',
      body: JSON.stringify({ instanceName })
    });
    return data.qr;
  },

  async connectInstance(instanceName: string, phoneNumber?: string): Promise<QrResponse> {
    const digits = phoneNumber ? phoneNumber.replace(/\D/g, '') : '';
    const query = digits ? `?number=${encodeURIComponent(digits)}` : '';
    return request<QrResponse>(`/api/whatsapp/instances/${encodeURIComponent(instanceName)}/connect${query}`);
  },

  async getStatus(instanceName: string): Promise<unknown> {
    const data = await request<{ success: boolean; status: unknown }>(`/api/whatsapp/instances/${encodeURIComponent(instanceName)}/status`);
    return data.status;
  },

  async sendTestMessage(instanceName: string, message: string, number?: string): Promise<{ success: boolean; number: string; result: unknown }> {
    return request<{ success: boolean; number: string; result: unknown }>(`/api/whatsapp/instances/${encodeURIComponent(instanceName)}/send-test`, {
      method: 'POST',
      body: JSON.stringify({ message, number })
    });
  },

  async logoutInstance(instanceName: string): Promise<void> {
    await request(`/api/whatsapp/instances/${encodeURIComponent(instanceName)}/logout`, { method: 'DELETE' });
  },

  async deleteInstance(instanceName: string): Promise<void> {
    await request(`/api/whatsapp/instances/${encodeURIComponent(instanceName)}/delete`, { method: 'DELETE' });
  }
};

export const getEvolutionInstanceName = (instance: EvolutionInstance) => {
  return instance.instance?.instanceName || instance.instanceName || instance.name || '';
};

export const getEvolutionInstanceState = (instance: EvolutionInstance) => {
  return instance.instance?.state || instance.state || instance.connectionStatus || 'desconhecido';
};

export const getEvolutionInstanceOwnerNumber = (instance: EvolutionInstance) => {
  const value =
    instance.owner ||
    (instance.instance as { owner?: string } | undefined)?.owner ||
    (instance.profileName ? '' : '');

  return String(value || '').replace(/@.*/, '').replace(/\D/g, '');
};

export const normalizeEvolutionConnectionState = (state: unknown) => {
  const raw = String(state || '').trim().toLowerCase();
  if (['open', 'connected', 'connectado', 'conectado'].includes(raw)) return 'connected';
  if (['connecting', 'qrcode', 'qr', 'pairing'].includes(raw)) return 'connecting';
  if (['close', 'closed', 'disconnected', 'logout'].includes(raw)) return 'disconnected';
  return raw || 'unknown';
};

export const getEvolutionConnectionLabel = (state: unknown) => {
  const normalized = normalizeEvolutionConnectionState(state);
  switch (normalized) {
    case 'connected':
      return 'Conectado';
    case 'connecting':
      return 'Aguardando conexao';
    case 'disconnected':
      return 'Desconectado';
    case 'unknown':
      return 'Desconhecido';
    default:
      return String(state || normalized);
  }
};

export const isEvolutionConnected = (state: unknown) => {
  return normalizeEvolutionConnectionState(state) === 'connected';
};
